import { Link, useNavigate } from "react-router-dom";
import styles from "./styles.module.scss";
import { useState, useEffect, useRef, ChangeEvent } from "react";
import Logo from "@assets/img/logo";
import ArrowLeftBlue from "@assets/icons/ArrowLeftBlue";
import ArrowRightWhite from "@assets/icons/ArrowRightWhite";
import { ROUTES } from "src/router/routes";
import classNames from "classnames";
import { Country } from "@entities/Country";
import { AmazonService } from "@services/amazon/amazon.service";
import Flag from "react-world-flags";
import CheckedIcon from "@assets/icons/CheckedIcon.tsx";
import UncheckIcon from "@assets/icons/UncheckIcon.tsx";
import SearchIcon from "@assets/icons/SearchIcon.tsx";
import ArrowDownIcon from "@assets/icons/ArrowDownIcon.tsx";
import UnselectIcon from "@assets/icons/UnselectIcon.tsx";

export const AmazonSellerAccount = () => {
  const [selectedRegions, setSelectedRegions] = useState<Country[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const nav = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await AmazonService.countries.getCountries();
        setCountries(response);
      } catch {
        console.error("Failed to fetch countries");
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredCountries = countries.filter((country) =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleDropdownToggle = () => {
    setDropdownOpen((open) => !open);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleApply = () => {
    setDropdownOpen(false);
  };

  const handleRegionToggle = (code: string, checked: boolean) => {
    setSelectedRegions((prev) =>
      checked
        ? [...prev, countries.find((c) => c.code === code)]
        : prev.filter((c) => c.code !== code),
    );
  };

  const handleRemoveRegion = (code: string) => {
    setSelectedRegions((prev) => prev.filter((c) => c.code !== code));
  };

  const handleSubmit = async () => {
    try {
      await AmazonService.countries.setSelectedCountries(
        selectedRegions.map((c) => c.code),
      );
    } catch {
      console.error("Failed to submit selected countries");
    }
  };

  const handleSkip = () => {
    nav("/account-success");
  };

  const selectedNames =
    selectedRegions.map((c) => c.name).join(", ") ?? "Select region";

  return (
    <div className={styles.page}>
      <Link to={ROUTES.ROOT}>
        <Logo color="black" />
      </Link>
      <h1>
        Connect to your Seller
        <br />
        Account
      </h1>
      <h2>Choose the Amazon marketplace region</h2>

      <div
        className={styles.customDropdown}
        style={{ marginBottom: selectedRegions.length > 0 ? "30px" : "182px" }}
        ref={dropdownRef}
      >
        <button
          type="button"
          className={styles.dropdownToggle}
          onClick={handleDropdownToggle}
        >
          {selectedNames}
          <span style={{ transform: `rotate(${dropdownOpen ? 180 : 0}deg)` }}>
            <ArrowDownIcon />
          </span>
        </button>

        {dropdownOpen && (
          <div className={styles.dropdownMenu}>
            <span className={styles.dropdownMenuSearchIcon}>
              <SearchIcon />
            </span>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              className={styles.dropdownSearch}
            />
            <ul className={styles.dropdownOptions}>
              {filteredCountries.map((country) => (
                <li key={country.code}>
                  <label className={styles.dropdownLabel}>
                    <input
                      type="checkbox"
                      value={country.code}
                      checked={selectedRegions.some(
                        (c) => c.code === country.code,
                      )}
                      onChange={(e) =>
                        handleRegionToggle(country.code, e.target.checked)
                      }
                      className={styles.checkboxInput}
                    />
                    <span className={styles.checkboxIcon}>
                      {selectedRegions.some((c) => c.code === country.code) ? (
                        <CheckedIcon />
                      ) : (
                        <UncheckIcon />
                      )}
                    </span>
                    <Flag
                      code={country.code}
                      style={{
                        width: 24,
                        height: 19,
                        borderRadius: 2,
                        objectFit: "cover",
                      }}
                    />
                    <span>{country.name}</span>
                  </label>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className={styles.applyBtn}
              onClick={handleApply}
            >
              APPLY
            </button>
          </div>
        )}
      </div>

      <div className={styles.marketplaceGrid}>
        {selectedRegions.map((marketplace) => (
          <label key={marketplace.code} className={classNames(styles.card)}>
            <div className={styles.inLine}>
              <span style={{ color: "black", marginBottom: "2px" }}>
                {marketplace.name}
              </span>
              <button
                type="button"
                onClick={() => handleRemoveRegion(marketplace.code)}
                className={styles.unselectIcon}
                aria-label={`Remove ${marketplace.name}`}
              >
                <UnselectIcon />
              </button>
            </div>
            <Flag
              code={marketplace.code}
              style={{ width: 95, height: 48, objectFit: "cover" }}
            />
          </label>
        ))}
      </div>

      <div className={styles.buttons}>
        <button className={styles.back} onClick={() => nav(-1)}>
          <ArrowLeftBlue /> Back
        </button>
        <button
          type="button"
          className={classNames(
            styles.connect,
            selectedRegions.length === 0 && styles.disabled,
          )}
          onClick={handleSubmit}
        >
          Connect to Seller Central <ArrowRightWhite />
        </button>
      </div>

      <button type="button" className={styles.skip} onClick={handleSkip}>
        Skip
      </button>
    </div>
  );
};
